import React, { FC, Suspense, useEffect, useMemo, useState, useTransition } from 'react';
import ContentLoader from 'react-content-loader';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Checkbox } from 'src/components/widgets/checkbox';
import { Drawer } from 'src/components/widgets/drawer';
import { ZSwitch } from 'src/components/widgets/zswitch';
import { useRelayStore } from 'src/relay/environment';
import { T, backofficeUrl, toLocalID } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import type { printingMethodEditor_PrintTypeDetailQuery } from './__generated__/printingMethodEditor_PrintTypeDetailQuery.graphql';
import type { printingMethodEditor_ProductCountQuery } from './__generated__/printingMethodEditor_ProductCountQuery.graphql';
import type { printingMethodEditor_UpdatePrintTypeMutation } from './__generated__/printingMethodEditor_UpdatePrintTypeMutation.graphql';
import {
    CheckboxGridCompact,
    ColorInputCompact,
    CompactSwitchGrid,
    CompactSwitchLabel,
    CompactSwitchRow,
    EffectsRow,
    FooterButton,
    FormDivider,
    FormFixedColumn,
    FormFlexColumn,
    FormFlexRow,
    FormFlexStartRow,
    FormGapRow,
    FormHelpDanger,
    FormInputCompact,
    FormLabelCompact,
    FormSelectCompact,
    FullModeHint,
    FullModeIframeContainer,
    FullModeLink,
    FullModeSkeleton,
    IframeEmbed,
    SectionCard,
    SectionCardTitle,
    SharedWarningCallout,
    StackedBody,
    StackedBreadcrumb,
    StackedFooterButtons,
    TextMarginBottom
} from '../common/styled';

export interface PrintingMethodEditorProps {
    productId: string;
    printTypeId: string;
    onBack: () => void;
    onGoToFullTool: () => void;
}

const printTypeDetailQuery = graphql`
    query printingMethodEditor_PrintTypeDetailQuery($id: ID!) {
        printType(id: $id) {
            id
            name
            dpi
            outputFileFormats
            maxColors
            restrictions {
                canAddText
                textBox
                canUseTextArt
                curvedText
                allowBold
                allowItalic
                uppercase
                allowChangeFontSize
                allowLetterSpacing
                allowLineSpacing
                allowTextStroke
                allowShadows
                defaultColor
                allowGradientTextColors
                allowImageUploadTextColors
                allowImageGalleryTextColors
                showImageProvider
                disableSellerImages
                disableUserImages
                canChangeSvgColors
                canUseImageFilters
                enableVectorize
                canStretchImages
                maxNrImages
                uploadRestrictions {
                    isUserImageAllowed
                    isPngAllowed
                    isJpgAllowed
                    isSvgAllowed
                    isPdfAllowed
                    isPdfWithRasterAllowed
                    isEpsAllowed
                    isAiAllowed
                    isFacebookAllowed
                    isInstagramAllowed
                    resizeLargePdf
                }
            }
            backgroundRemoverSettings {
                isUploadEnabled
                isHiddenMode
                isEditEnabled
            }
            patternSetting {
                presets
                isTextEnabled
                isImageEnabled
                allowSpacingChange
                allowOffsetChange
            }
            previewDesignEffect {
                type
                blendMode
                enhanceBlendMode
                opacity
                textColor
                imageEffectTypeName
                imageEffectParameterValues {
                    name
                    value
                }
            }
            outputDesignEffect {
                type
                blendMode
                enhanceBlendMode
                opacity
                textColor
                imageEffectTypeName
                imageEffectParameterValues {
                    name
                    value
                }
            }
        }
        designEffectTemplates {
            id
            name
            previewDesignEffect {
                type
            }
        }
    }
`;

const productNameQuery = graphql`
    query printingMethodEditor_ProductNameQuery($id: ID!) {
        node(id: $id) {
            ... on Model {
                name
            }
        }
    }
`;

const PrintTypeProductCountCallout: FC<{ printTypeId: string }> = ({ printTypeId }) => {
    const data = useLazyLoadQuery<printingMethodEditor_ProductCountQuery>(
        graphql`
            query printingMethodEditor_ProductCountQuery($id: ID!) {
                printType(id: $id) {
                    models {
                        modelID
                    }
                }
            }
        `,
        { id: printTypeId }
    );
    const count = data?.printType?.models?.length;
    if (count == null || count <= 1) return null;
    return (
        <SharedWarningCallout>
            {T._('This printing method is assigned to', 'Admin')} <strong>{count}</strong> {T._('products. Any changes will apply to all of them.', 'Admin')}
        </SharedWarningCallout>
    );
};

const PrintingMethodEditorContent: FC<PrintingMethodEditorProps> = ({ productId, printTypeId, onBack, onGoToFullTool }) => {
    const json = useLazyLoadQuery<printingMethodEditor_PrintTypeDetailQuery>(printTypeDetailQuery, { id: printTypeId });
    const productData = useLazyLoadQuery<any>(productNameQuery, { id: productId });
    const productName = (productData?.node as any)?.name || '';
    const invalidate = useRelayStore((s) => s.invalidateRelayStore);
    const [fullMode, setFullMode] = useState(false);
    const [iframeLoading, setIframeLoading] = useState(true);
    const [isSaving, startSaveTransition] = useTransition();

    useEffect(() => {
        const handleMessage = (e: MessageEvent) => {
            if (e.data?.type === 'embedded-tool-saved') {
                onBack();
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [onBack]);

    useEffect(() => {
        if (!fullMode) return;
        const style = document.createElement('style');
        style.textContent = '* { overflow: hidden !important; }';
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, [fullMode]);

    const [commitUpdatePrintType] = useMutation<printingMethodEditor_UpdatePrintTypeMutation>(graphql`
        mutation printingMethodEditor_UpdatePrintTypeMutation($input: PrintTypeUpdateInput!) {
            updatePrintType(input: $input) {
                printType {
                    id
                }
            }
        }
    `);

    const printType = json?.printType;
    const methodName = printType?.name || '';
    const restrictions = printType?.restrictions;
    const uploadRestrictions = restrictions?.uploadRestrictions;

    // Print-ready files
    const initFormats = useMemo(() => (printType?.outputFileFormats || []).map((format: string) => format.toUpperCase()), [printType]);
    const [outputPNG, setOutputPNG] = useState(() => initFormats.includes('PNG'));
    const [outputSVG, setOutputSVG] = useState(() => initFormats.includes('SVG'));
    const [outputPDF, setOutputPDF] = useState(() => initFormats.includes('PDF'));
    const [outputDXF, setOutputDXF] = useState(() => initFormats.includes('DXF'));
    const [dpi, setDpi] = useState(() => printType?.dpi?.toString() || '300');
    // Colors
    const [colorLimit, setColorLimit] = useState(() => printType?.maxColors != null && printType.maxColors > 0);
    const [maxColors, setMaxColors] = useState(() => printType?.maxColors?.toString() || '5');

    // Text
    const [allowText, setAllowText] = useState(() => restrictions?.canAddText !== false);
    const [textBox, setTextBox] = useState(() => restrictions?.textBox !== false);
    const [textArt, setTextArt] = useState(() => restrictions?.canUseTextArt !== false);
    const [curvedText, setCurvedText] = useState(() => !!restrictions?.curvedText);
    const [bold, setBold] = useState(() => restrictions?.allowBold !== false);
    const [italic, setItalic] = useState(() => restrictions?.allowItalic !== false);
    const [uppercase, setUppercase] = useState(() => !!restrictions?.uppercase);
    const [allowTextResize, setAllowTextResize] = useState(() => restrictions?.allowChangeFontSize !== false);
    const [letterSpacing, setLetterSpacing] = useState(() => restrictions?.allowLetterSpacing !== false);
    const [lineSpacing, setLineSpacing] = useState(() => restrictions?.allowLineSpacing !== false);
    const [textStroke, setTextStroke] = useState(() => !!restrictions?.allowTextStroke);
    const [allowShadows, setAllowShadows] = useState(() => !!restrictions?.allowShadows);
    const [defaultTextColor, setDefaultTextColor] = useState(() => restrictions?.defaultColor || '#287fb9');
    // Fill text
    const [fillTextGradient, setFillTextGradient] = useState(() => !!restrictions?.allowGradientTextColors);
    const [fillTextImageUpload, setFillTextImageUpload] = useState(() => !!restrictions?.allowImageUploadTextColors);
    const [fillTextImageGallery, setFillTextImageGallery] = useState(() => !!restrictions?.allowImageGalleryTextColors);

    // Images
    const [allowGallery, setAllowGallery] = useState(() => !restrictions?.disableSellerImages);
    const [allowUpload, setAllowUpload] = useState(() => !restrictions?.disableUserImages);
    const [uploadPNG, setUploadPNG] = useState(() => uploadRestrictions?.isPngAllowed !== false);
    const [uploadJPG, setUploadJPG] = useState(() => uploadRestrictions?.isJpgAllowed !== false);
    const [uploadSVG, setUploadSVG] = useState(() => uploadRestrictions?.isSvgAllowed !== false);
    const [uploadPDF, setUploadPDF] = useState(() => uploadRestrictions?.isPdfAllowed !== false);
    const [uploadEPS, setUploadEPS] = useState(() => uploadRestrictions?.isEpsAllowed !== false);
    const [uploadAI, setUploadAI] = useState(() => uploadRestrictions?.isAiAllowed !== false);
    const [maxNrImages, setMaxNrImages] = useState(() => restrictions?.maxNrImages?.toString() || '');
    const [canChangeSvgColors, setCanChangeSvgColors] = useState(() => restrictions?.canChangeSvgColors !== false);
    const [canUseFilters, setCanUseFilters] = useState(() => restrictions?.canUseImageFilters !== false);
    const [aiVectorizer, setAiVectorizer] = useState(() => !!restrictions?.enableVectorize);
    const [bgRemover, setBgRemover] = useState(() => !!printType?.backgroundRemoverSettings?.isUploadEnabled);
    const [canStretchImages, setCanStretchImages] = useState(() => !!restrictions?.canStretchImages);
    // Repeated pattern
    const [patternText, setPatternText] = useState(() => !!printType?.patternSetting?.isTextEnabled);
    const [patternImage, setPatternImage] = useState(() => !!printType?.patternSetting?.isImageEnabled);

    // Effects
    const [effectTemplate, setEffectTemplate] = useState(() => {
        const effectType = printType?.previewDesignEffect?.type;
        if (effectType === 1) return 'ENGRAVING';
        if (effectType === 2) return 'WOOD';
        if (effectType === 3) return 'PIXELATION';
        return 'NONE';
    });
    const [effectPresets] = useState<{ id: string; name: string; type: number }[]>(() =>
        (json?.designEffectTemplates || []).map((t: { id: string; name: string; previewDesignEffect: { type: number } }) => ({
            id: t.id,
            name: t.name,
            type: t.previewDesignEffect.type
        }))
    );
    const [selectedEffectPreset, setSelectedEffectPreset] = useState('');

    const hasChanges = useMemo(() => {
        // Print-ready files
        if (outputPNG !== initFormats.includes('PNG')) return true;
        if (outputSVG !== initFormats.includes('SVG')) return true;
        if (outputPDF !== initFormats.includes('PDF')) return true;
        if (outputDXF !== initFormats.includes('DXF')) return true;
        if (dpi !== (printType?.dpi?.toString() || '300')) return true;
        // Colors
        if (colorLimit !== (printType?.maxColors != null && printType.maxColors > 0)) return true;
        if (maxColors !== (printType?.maxColors?.toString() || '5')) return true;
        // Text
        if (allowText !== (restrictions?.canAddText !== false)) return true;
        if (textBox !== (restrictions?.textBox !== false)) return true;
        if (textArt !== (restrictions?.canUseTextArt !== false)) return true;
        if (curvedText !== !!restrictions?.curvedText) return true;
        if (bold !== (restrictions?.allowBold !== false)) return true;
        if (italic !== (restrictions?.allowItalic !== false)) return true;
        if (uppercase !== !!restrictions?.uppercase) return true;
        if (allowTextResize !== (restrictions?.allowChangeFontSize !== false)) return true;
        if (letterSpacing !== (restrictions?.allowLetterSpacing !== false)) return true;
        if (lineSpacing !== (restrictions?.allowLineSpacing !== false)) return true;
        if (textStroke !== !!restrictions?.allowTextStroke) return true;
        if (allowShadows !== !!restrictions?.allowShadows) return true;
        if (defaultTextColor !== (restrictions?.defaultColor || '#287fb9')) return true;
        // Fill text
        if (fillTextGradient !== !!restrictions?.allowGradientTextColors) return true;
        if (fillTextImageUpload !== !!restrictions?.allowImageUploadTextColors) return true;
        if (fillTextImageGallery !== !!restrictions?.allowImageGalleryTextColors) return true;
        // Images
        if (allowGallery !== !restrictions?.disableSellerImages) return true;
        if (allowUpload !== !restrictions?.disableUserImages) return true;
        if (uploadPNG !== (uploadRestrictions?.isPngAllowed !== false)) return true;
        if (uploadJPG !== (uploadRestrictions?.isJpgAllowed !== false)) return true;
        if (uploadSVG !== (uploadRestrictions?.isSvgAllowed !== false)) return true;
        if (uploadPDF !== (uploadRestrictions?.isPdfAllowed !== false)) return true;
        if (uploadEPS !== (uploadRestrictions?.isEpsAllowed !== false)) return true;
        if (uploadAI !== (uploadRestrictions?.isAiAllowed !== false)) return true;
        if (maxNrImages !== (restrictions?.maxNrImages?.toString() || '')) return true;
        if (canChangeSvgColors !== (restrictions?.canChangeSvgColors !== false)) return true;
        if (canUseFilters !== (restrictions?.canUseImageFilters !== false)) return true;
        if (aiVectorizer !== !!restrictions?.enableVectorize) return true;
        if (bgRemover !== !!printType?.backgroundRemoverSettings?.isUploadEnabled) return true;
        if (canStretchImages !== !!restrictions?.canStretchImages) return true;
        // Repeated pattern
        if (patternText !== !!printType?.patternSetting?.isTextEnabled) return true;
        if (patternImage !== !!printType?.patternSetting?.isImageEnabled) return true;
        // Effects
        const initEffectType = printType?.previewDesignEffect?.type;
        const initEffectTemplate = initEffectType === 1 ? 'ENGRAVING' : initEffectType === 2 ? 'WOOD' : initEffectType === 3 ? 'PIXELATION' : 'NONE';
        if (effectTemplate !== initEffectTemplate) return true;
        return false;
    }, [
        outputPNG,
        outputSVG,
        outputPDF,
        outputDXF,
        dpi,
        colorLimit,
        maxColors,
        allowText,
        textBox,
        textArt,
        curvedText,
        bold,
        italic,
        uppercase,
        allowTextResize,
        letterSpacing,
        lineSpacing,
        textStroke,
        allowShadows,
        defaultTextColor,
        fillTextGradient,
        fillTextImageUpload,
        fillTextImageGallery,
        allowGallery,
        allowUpload,
        uploadPNG,
        uploadJPG,
        uploadSVG,
        uploadPDF,
        uploadEPS,
        uploadAI,
        maxNrImages,
        canChangeSvgColors,
        canUseFilters,
        aiVectorizer,
        bgRemover,
        canStretchImages,
        patternText,
        patternImage,
        effectTemplate,
        initFormats,
        printType,
        restrictions,
        uploadRestrictions
    ]);

    const buildDesignEffect = (
        effect: {
            readonly type: number;
            readonly blendMode: string | null;
            readonly enhanceBlendMode: boolean;
            readonly opacity: number;
            readonly textColor: string | null;
            readonly imageEffectTypeName: string | null;
            readonly imageEffectParameterValues: ReadonlyArray<{ readonly name: string; readonly value: string }> | null;
        } | null
    ) => {
        if (!effect) return null;
        return {
            type: { value: effect.type },
            blendMode: { value: effect.blendMode || 'NORMAL' },
            enhanceBlendMode: { value: effect.enhanceBlendMode ?? false },
            opacity: { value: effect.opacity ?? 1 },
            textColor: { value: effect.textColor || null },
            imageEffectTypeName: { value: effect.imageEffectTypeName || '' },
            imageEffectParameterValues: {
                value: (effect.imageEffectParameterValues || []).map((param: { name: string; value: string }) => ({
                    name: param.name,
                    value: param.value
                }))
            }
        };
    };

    const handleSave = () => {
        if (!printType) return;

        const outputFileFormats: Array<'PNG' | 'SVG' | 'PDF' | 'DXF'> = [];
        if (outputPNG) outputFileFormats.push('PNG');
        if (outputSVG) outputFileFormats.push('SVG');
        if (outputPDF) outputFileFormats.push('PDF');
        if (outputDXF) outputFileFormats.push('DXF');

        const effectTypeMap: Record<string, number> = { ENGRAVING: 1, WOOD: 2, PIXELATION: 3 };
        const effectType = effectTypeMap[effectTemplate] ?? null;
        let previewDesignEffect = buildDesignEffect(printType.previewDesignEffect);
        if (effectType != null && previewDesignEffect) {
            previewDesignEffect = { ...previewDesignEffect, type: { value: effectType } };
        } else if (effectType != null) {
            previewDesignEffect = {
                type: { value: effectType },
                blendMode: { value: 'NORMAL' },
                enhanceBlendMode: { value: false },
                opacity: { value: 1 },
                textColor: { value: null },
                imageEffectTypeName: { value: '' },
                imageEffectParameterValues: { value: [] }
            };
        } else {
            previewDesignEffect = null;
        }

        const input = {
            id: printType.id,
            dpi: { value: parseInt(dpi) || 300 },
            outputFileFormats: { value: outputFileFormats },
            maxColors: { value: colorLimit && parseInt(maxColors) ? parseInt(maxColors) : null },
            restrictions: {
                value: {
                    canAddText: { value: allowText },
                    textBox: { value: textBox },
                    canUseTextArt: { value: textArt },
                    curvedText: { value: curvedText },
                    allowBold: { value: bold },
                    allowItalic: { value: italic },
                    uppercase: { value: uppercase },
                    allowChangeFontSize: { value: allowTextResize },
                    allowLetterSpacing: { value: letterSpacing },
                    allowLineSpacing: { value: lineSpacing },
                    allowTextStroke: { value: textStroke },
                    allowShadows: { value: allowShadows },
                    defaultColor: { value: defaultTextColor || null },
                    allowGradientTextColors: { value: fillTextGradient },
                    allowImageUploadTextColors: { value: fillTextImageUpload },
                    allowImageGalleryTextColors: { value: fillTextImageGallery },
                    showImageProvider: { value: allowUpload },
                    disableSellerImages: { value: !allowGallery },
                    disableUserImages: { value: !allowUpload },
                    canChangeSvgColors: { value: canChangeSvgColors },
                    canUseImageFilters: { value: canUseFilters },
                    enableVectorize: { value: aiVectorizer },
                    canStretchImages: { value: canStretchImages },
                    maxNrImages: { value: maxNrImages && parseInt(maxNrImages) ? parseInt(maxNrImages) : null },
                    uploadRestrictions: {
                        value: {
                            isUserImageAllowed: uploadRestrictions?.isUserImageAllowed !== false,
                            isPngAllowed: uploadPNG,
                            isJpgAllowed: uploadJPG,
                            isSvgAllowed: uploadSVG,
                            isPdfAllowed: uploadPDF,
                            isPdfWithRasterAllowed: uploadRestrictions?.isPdfWithRasterAllowed ?? false,
                            isEpsAllowed: uploadEPS,
                            isAiAllowed: uploadAI,
                            isFacebookAllowed: uploadRestrictions?.isFacebookAllowed ?? false,
                            isInstagramAllowed: uploadRestrictions?.isInstagramAllowed ?? false,
                            resizeLargePdf: uploadRestrictions?.resizeLargePdf ?? false
                        }
                    }
                }
            },
            previewDesignEffect: { value: previewDesignEffect },
            outputDesignEffect: { value: buildDesignEffect(printType.outputDesignEffect) },
            backgroundRemoverSettings: {
                value: bgRemover
                    ? {
                          isUploadEnabled: { value: true },
                          isHiddenMode: { value: printType.backgroundRemoverSettings?.isHiddenMode ?? false },
                          isEditEnabled: { value: printType.backgroundRemoverSettings?.isEditEnabled ?? false }
                      }
                    : null
            },
            patternSetting: {
                value:
                    patternText || patternImage
                        ? {
                              presets: { value: (printType.patternSetting?.presets || []).filter(Boolean) as number[] },
                              isTextEnabled: { value: patternText },
                              isImageEnabled: { value: patternImage },
                              allowSpacingChange: { value: printType.patternSetting?.allowSpacingChange ?? false },
                              allowOffsetChange: { value: printType.patternSetting?.allowOffsetChange ?? false }
                          }
                        : null
            }
        };

        commitUpdatePrintType({
            variables: { input },
            updater: (store) => store.invalidateStore(),
            onCompleted: () => {
                invalidate();
                startSaveTransition(() => {
                    onBack();
                });
            }
        });
    };

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={{ title: methodName, subtitle: fullMode ? T._('Printing method — Full mode', 'Admin') : T._('Printing method', 'Admin') }}
            width={fullMode ? window.innerWidth - 80 : 620}
            footer={
                <>
                    {!fullMode && <FullModeHint>{T._('+ 20 more settings available in full mode', 'Admin')}</FullModeHint>}
                    <FullModeLink
                        onClick={() => {
                            setFullMode(!fullMode);
                            if (!fullMode) setIframeLoading(true);
                        }}
                    >
                        {fullMode ? '↙ ' + T._('Quick mode', 'Admin') : '↗ ' + T._('Full mode', 'Admin')}
                    </FullModeLink>
                    {!fullMode && (
                        <StackedFooterButtons>
                            <FooterButton onClick={onBack} disabled={isSaving}>
                                {T._('Cancel', 'Admin')}
                            </FooterButton>
                            <FooterButton $primary onClick={handleSave} disabled={isSaving || !hasChanges}>
                                {T._('Save Changes', 'Admin')}
                            </FooterButton>
                        </StackedFooterButtons>
                    )}
                </>
            }
        >
            {fullMode ? (
                <FullModeIframeContainer>
                    {iframeLoading && (
                        <FullModeSkeleton>
                            <ContentLoader
                                speed={2}
                                width='100%'
                                height='516'
                                backgroundColor={theme.skeleton.backgroundColor}
                                foregroundColor={theme.skeleton.foregroundColor}
                            >
                                <rect x='0' y='0' rx='8' ry='8' width='30%' height='28' />
                                <rect x='0' y='52' rx='8' ry='8' width='100%' height='16' />
                                <rect x='0' y='76' rx='8' ry='8' width='80%' height='16' />
                                <rect x='0' y='116' rx='8' ry='8' width='60%' height='40' />
                                <rect x='0' y='180' rx='8' ry='8' width='100%' height='16' />
                                <rect x='0' y='204' rx='8' ry='8' width='90%' height='16' />
                                <rect x='0' y='244' rx='8' ry='8' width='45%' height='40' />
                                <rect x='0' y='316' rx='8' ry='8' width='100%' height='200' />
                            </ContentLoader>
                        </FullModeSkeleton>
                    )}
                    <IframeEmbed
                        src={backofficeUrl('/printing-method-embedded/' + toLocalID(printTypeId)) + '?embedded=true'}
                        $loading={iframeLoading}
                        title={methodName}
                        onLoad={() => setIframeLoading(false)}
                    />
                </FullModeIframeContainer>
            ) : (
                <>
                    <StackedBreadcrumb>
                        <span onClick={onBack}>{productName}</span> / {T._('Printing methods', 'Admin')} / {methodName}
                    </StackedBreadcrumb>

                    <StackedBody>
                        <PrintTypeProductCountCallout printTypeId={printTypeId} />

                        {/* ---- PRINT-READY FILES ---- */}
                        <SectionCard>
                            <SectionCardTitle>{T._('Print-ready files', 'Admin')}</SectionCardTitle>
                            <FormFlexStartRow>
                                <FormFlexColumn>
                                    <FormLabelCompact>{T._('Output formats', 'Admin')}</FormLabelCompact>
                                    <CheckboxGridCompact $columns={4}>
                                        <Checkbox checked={outputPNG} label='PNG' variant='compact' onChange={() => setOutputPNG(!outputPNG)} />
                                        <Checkbox checked={outputSVG} label='SVG' variant='compact' onChange={() => setOutputSVG(!outputSVG)} />
                                        <Checkbox checked={outputPDF} label='PDF' variant='compact' onChange={() => setOutputPDF(!outputPDF)} />
                                        <Checkbox checked={outputDXF} label='DXF' variant='compact' onChange={() => setOutputDXF(!outputDXF)} />
                                    </CheckboxGridCompact>
                                </FormFlexColumn>
                                <FormFixedColumn $width={80}>
                                    <FormLabelCompact>{T._('DPI', 'Admin')}</FormLabelCompact>
                                    <FormInputCompact type='number' value={dpi} onChange={(e) => setDpi(e.target.value)} $width={80} />
                                </FormFixedColumn>
                            </FormFlexStartRow>
                            {outputPNG && Number(dpi) >= 2000 && <FormHelpDanger>{T._('High DPI values may affect performance', 'Admin')}</FormHelpDanger>}
                        </SectionCard>

                        {/* ---- COLORS ---- */}
                        <SectionCard>
                            <SectionCardTitle>
                                {T._('Color limit', 'Admin')}
                                <ZSwitch checked={colorLimit} onChange={() => setColorLimit(!colorLimit)} />
                            </SectionCardTitle>
                            {colorLimit && (
                                <FormFlexRow>
                                    <CompactSwitchLabel>{T._('Max colors', 'Admin')}</CompactSwitchLabel>
                                    <FormInputCompact type='number' value={maxColors} onChange={(e) => setMaxColors(e.target.value)} $width={80} />
                                </FormFlexRow>
                            )}
                        </SectionCard>

                        {/* ---- TEXT ---- */}
                        <SectionCard>
                            <SectionCardTitle>
                                {T._('Text', 'Admin')}
                                <ZSwitch checked={allowText} onChange={() => setAllowText(!allowText)} />
                            </SectionCardTitle>

                            {allowText && (
                                <>
                                    {/* Text styles as inline chips */}
                                    <TextMarginBottom>
                                        <CheckboxGridCompact $columns={4}>
                                            <Checkbox checked={textArt} label='TextArt' variant='compact' onChange={() => setTextArt(!textArt)} />
                                            <Checkbox
                                                checked={curvedText}
                                                label={T._('Curved', 'Admin')}
                                                variant='compact'
                                                onChange={() => setCurvedText(!curvedText)}
                                            />
                                            <Checkbox checked={bold} label={T._('Bold', 'Admin')} variant='compact' onChange={() => setBold(!bold)} />
                                            <Checkbox checked={italic} label={T._('Italic', 'Admin')} variant='compact' onChange={() => setItalic(!italic)} />
                                        </CheckboxGridCompact>
                                    </TextMarginBottom>

                                    {/* Default color */}
                                    <FormDivider>
                                        <FormLabelCompact>{T._('Default color', 'Admin')}</FormLabelCompact>
                                        <ColorInputCompact type='color' value={defaultTextColor} onChange={(e) => setDefaultTextColor(e.target.value)} />
                                    </FormDivider>

                                    {/* Text options as vertical checkbox list */}
                                    <FormDivider>
                                        <CheckboxGridCompact $columns={1}>
                                            <Checkbox
                                                checked={allowTextResize}
                                                label={T._('Resize', 'Admin')}
                                                variant='compact'
                                                onChange={() => setAllowTextResize(!allowTextResize)}
                                            />
                                            <Checkbox
                                                checked={letterSpacing}
                                                label={T._('Letter spacing', 'Admin')}
                                                variant='compact'
                                                onChange={() => setLetterSpacing(!letterSpacing)}
                                            />
                                            <Checkbox
                                                checked={lineSpacing}
                                                label={T._('Line spacing', 'Admin')}
                                                variant='compact'
                                                onChange={() => setLineSpacing(!lineSpacing)}
                                            />
                                            <Checkbox
                                                checked={textStroke}
                                                label={T._('Stroke', 'Admin')}
                                                variant='compact'
                                                onChange={() => setTextStroke(!textStroke)}
                                            />
                                        </CheckboxGridCompact>
                                    </FormDivider>

                                    {/* Fill text */}
                                    <FormDivider>
                                        <FormLabelCompact>{T._('Fill text', 'Admin')}</FormLabelCompact>
                                        <CheckboxGridCompact $columns={1}>
                                            <Checkbox
                                                checked={fillTextGradient}
                                                label={T._('Gradient', 'Admin')}
                                                variant='compact'
                                                onChange={() => setFillTextGradient(!fillTextGradient)}
                                            />
                                            <Checkbox
                                                checked={fillTextImageUpload}
                                                label={T._('Image upload', 'Admin')}
                                                variant='compact'
                                                onChange={() => setFillTextImageUpload(!fillTextImageUpload)}
                                            />
                                            <Checkbox
                                                checked={fillTextImageGallery}
                                                label={T._('Image gallery', 'Admin')}
                                                variant='compact'
                                                onChange={() => setFillTextImageGallery(!fillTextImageGallery)}
                                            />
                                        </CheckboxGridCompact>
                                    </FormDivider>
                                </>
                            )}
                        </SectionCard>

                        {/* ---- IMAGES ---- */}
                        <SectionCard>
                            <SectionCardTitle>{T._('Images', 'Admin')}</SectionCardTitle>

                            <CompactSwitchGrid>
                                <CompactSwitchRow>
                                    <CompactSwitchLabel>{T._('Image and Clipart Gallery', 'Admin')}</CompactSwitchLabel>
                                    <ZSwitch checked={allowGallery} onChange={() => setAllowGallery(!allowGallery)} />
                                </CompactSwitchRow>
                            </CompactSwitchGrid>
                            <FormDivider>
                                <CompactSwitchGrid>
                                    <CompactSwitchRow>
                                        <CompactSwitchLabel>{T._('User upload', 'Admin')}</CompactSwitchLabel>
                                        <ZSwitch checked={allowUpload} onChange={() => setAllowUpload(!allowUpload)} />
                                    </CompactSwitchRow>
                                </CompactSwitchGrid>
                            </FormDivider>

                            {allowUpload && (
                                <TextMarginBottom>
                                    <FormLabelCompact style={{ marginTop: 8 }}>{T._('Accepted formats', 'Admin')}</FormLabelCompact>
                                    <CheckboxGridCompact $columns={3}>
                                        <Checkbox checked={uploadPNG} label='PNG' variant='compact' onChange={() => setUploadPNG(!uploadPNG)} />
                                        <Checkbox checked={uploadJPG} label='JPG' variant='compact' onChange={() => setUploadJPG(!uploadJPG)} />
                                        <Checkbox checked={uploadSVG} label='SVG' variant='compact' onChange={() => setUploadSVG(!uploadSVG)} />
                                        <Checkbox checked={uploadPDF} label='PDF' variant='compact' onChange={() => setUploadPDF(!uploadPDF)} />
                                        <Checkbox checked={uploadEPS} label='EPS' variant='compact' onChange={() => setUploadEPS(!uploadEPS)} />
                                        <Checkbox checked={uploadAI} label='AI' variant='compact' onChange={() => setUploadAI(!uploadAI)} />
                                    </CheckboxGridCompact>
                                </TextMarginBottom>
                            )}

                            {/* Image features */}
                            {allowUpload && (
                                <CompactSwitchGrid>
                                    <CompactSwitchRow>
                                        <CompactSwitchLabel>{T._('AI Vectorizer', 'Admin')}</CompactSwitchLabel>
                                        <ZSwitch checked={aiVectorizer} onChange={() => setAiVectorizer(!aiVectorizer)} />
                                    </CompactSwitchRow>
                                    <CompactSwitchRow>
                                        <CompactSwitchLabel>{T._('AI Background Remover', 'Admin')}</CompactSwitchLabel>
                                        <ZSwitch checked={bgRemover} onChange={() => setBgRemover(!bgRemover)} />
                                    </CompactSwitchRow>
                                </CompactSwitchGrid>
                            )}
                            {(allowGallery || allowUpload) && (
                                <FormDivider>
                                    <Checkbox
                                        checked={canStretchImages}
                                        label={T._('Image stretch', 'Admin')}
                                        variant='compact'
                                        onChange={() => setCanStretchImages(!canStretchImages)}
                                    />
                                </FormDivider>
                            )}
                        </SectionCard>

                        {/* ---- TOOLS ---- */}
                        <SectionCard>
                            <SectionCardTitle>
                                {T._('Repeated pattern', 'Admin')}
                                <ZSwitch
                                    checked={patternText || patternImage}
                                    onChange={() => {
                                        if (patternText || patternImage) {
                                            setPatternText(false);
                                            setPatternImage(false);
                                        } else {
                                            setPatternText(true);
                                            setPatternImage(true);
                                        }
                                    }}
                                />
                            </SectionCardTitle>
                            {(patternText || patternImage) && (
                                <>
                                    <FormGapRow>
                                        <Checkbox
                                            checked={patternText}
                                            label={T._('Text', 'Admin')}
                                            variant='compact'
                                            onChange={() => setPatternText(!patternText)}
                                        />
                                        <Checkbox
                                            checked={patternImage}
                                            label={T._('Images', 'Admin')}
                                            variant='compact'
                                            onChange={() => setPatternImage(!patternImage)}
                                        />
                                    </FormGapRow>
                                </>
                            )}
                        </SectionCard>

                        {/* ---- EFFECTS ---- */}
                        <SectionCard>
                            <SectionCardTitle>{T._('Effects', 'Admin')}</SectionCardTitle>
                            <EffectsRow>
                                <FormSelectCompact
                                    value={effectTemplate}
                                    onChange={(e) => {
                                        setEffectTemplate(e.target.value);
                                        setSelectedEffectPreset('');
                                    }}
                                >
                                    <option value='NONE'>{T._('None', 'Admin')}</option>
                                    <option value='ENGRAVING'>{T._('Engraving', 'Admin')}</option>
                                    <option value='WOOD'>{T._('Wood', 'Admin')}</option>
                                    <option value='PIXELATION'>{T._('Pixelation', 'Admin')}</option>
                                </FormSelectCompact>
                                {effectTemplate !== 'NONE' && effectTemplate !== 'PIXELATION' && (
                                    <FormSelectCompact value={selectedEffectPreset} onChange={(e) => setSelectedEffectPreset(e.target.value)}>
                                        <option value=''>{T._('No preset', 'Admin')}</option>
                                        {effectPresets
                                            .filter((preset) => {
                                                const typeMap: Record<string, number> = { ENGRAVING: 1, WOOD: 2, PIXELATION: 3 };
                                                return preset.type === typeMap[effectTemplate];
                                            })
                                            .map((preset) => (
                                                <option key={preset.id} value={preset.id}>
                                                    {preset.name}
                                                </option>
                                            ))}
                                    </FormSelectCompact>
                                )}
                            </EffectsRow>
                        </SectionCard>
                    </StackedBody>
                </>
            )}
        </Drawer>
    );
};

export const PrintingMethodEditor: FC<PrintingMethodEditorProps> = (props) => {
    return (
        <Suspense
            fallback={
                <Drawer isOpen={true} isStacked onClose={props.onBack} header={{ title: '', subtitle: T._('Printing method', 'Admin') }} width={620}>
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='120'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='56' rx='8' ry='8' width='60%' height='40' />
                        </ContentLoader>
                    </StackedBody>
                </Drawer>
            }
        >
            <PrintingMethodEditorContent {...props} />
        </Suspense>
    );
};
